'use client';

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { EntityLookup } from '@/components/lookups/EntityLookup';
import { Building2, X, User } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { authenticatedApiClient } from '@/lib/api/client';
import { ASSIGNMENT_ROLE_OPTIONS } from '@sizzlenz/shared';
import type { ApiConnection } from '@/lib/hooks/useConnections';
import type { LookupOption } from '@/components/ui/lookup';
import type { ApiEntity } from '@/lib/api/types';

interface RepresentingEntity {
  id: number;
  name: string;
}

interface EditAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connection: ApiConnection | null;
}

export function EditAssignmentDialog({
  open,
  onOpenChange,
  connection,
}: EditAssignmentDialogProps) {
  const queryClient = useQueryClient();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [representing, setRepresenting] = useState<RepresentingEntity[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync form state when connection changes
  useEffect(() => {
    if (connection) {
      const roles: string[] = connection.data?.relationships || [];
      setSelectedRoles(roles);
      setNotes(connection.data?.notes || '');
      setRepresenting(connection.data?.representing || []);
    }
  }, [connection]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !isSubmitting) {
      // Reset handled by effect on next open
    }
    onOpenChange(newOpen);
  };

  const handleToggleRole = (role: string, checked: boolean) => {
    setSelectedRoles((prev) => (checked ? [...prev, role] : prev.filter((r) => r !== role)));
  };

  const handleAddEntity = (option: LookupOption<ApiEntity> | null) => {
    if (!option) return;
    if (representing.some((e) => e.id === option.data.id)) return;
    setRepresenting((prev) => [
      ...prev,
      { id: option.data.id, name: option.data.name_1 || `Entity ${option.data.id}` },
    ]);
  };

  const handleRemoveEntity = (entityId: number) => {
    setRepresenting((prev) => prev.filter((e) => e.id !== entityId));
  };

  const handleSubmit = async () => {
    if (!connection) return;

    if (selectedRoles.length === 0) {
      toast.error('Select at least one role');
      return;
    }

    setIsSubmitting(true);
    try {
      await authenticatedApiClient
        .patch(`connections/${connection.id}`, {
          json: {
            relationships: selectedRoles,
            notes: notes.trim() || undefined,
            representing: representing.length > 0 ? representing : undefined,
          },
        })
        .json();

      toast.success('Assignment updated');

      // Optimistic: update in cache
      queryClient.setQueriesData({ queryKey: ['connections'] }, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            connections: page.connections.map((c: any) =>
              c.id === connection.id
                ? {
                    ...c,
                    data: {
                      ...c.data,
                      role: selectedRoles[0],
                      relationships: selectedRoles,
                      notes: notes.trim() || undefined,
                      representing: representing.length > 0 ? representing : undefined,
                    },
                  }
                : c
            ),
          })),
        };
      });

      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAssigneeName = () => {
    if (!connection) return '';
    const fromEntity = (connection as any).fromEntity;
    const fromUser = (connection as any).fromUser;
    if (connection.fromType === 'user' && fromUser) {
      return fromUser.name || fromUser.email || `User ${connection.fromId}`;
    }
    if (connection.fromType === 'entity' && fromEntity) {
      return fromEntity.name_1 || `Entity ${connection.fromId}`;
    }
    return `${connection.fromType} ${connection.fromId}`;
  };

  const getAssigneeImage = () => {
    if (!connection) return null;
    const fromEntity = (connection as any).fromEntity;
    const fromUser = (connection as any).fromUser;
    if (connection.fromType === 'user' && fromUser) return fromUser.image;
    if (connection.fromType === 'entity' && fromEntity) return fromEntity.image;
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Assignment</DialogTitle>
          <DialogDescription>
            Update roles and representing entities for {getAssigneeName()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Assignee identity banner */}
          {connection && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                {getAssigneeImage() ? (
                  <img
                    src={getAssigneeImage()!}
                    alt={getAssigneeName()}
                    className="h-full w-full object-cover"
                  />
                ) : connection.fromType === 'entity' ? (
                  <Building2 className="h-5 w-5 text-gray-500" />
                ) : (
                  <User className="h-5 w-5 text-gray-500" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">{getAssigneeName()}</p>
                <p className="text-xs text-gray-500 capitalize">{connection.fromType}</p>
              </div>
            </div>
          )}
          {/* Role Checkboxes */}
          <div className="space-y-2">
            <Label>Roles (select at least one)</Label>
            <div className="space-y-1">
              {ASSIGNMENT_ROLE_OPTIONS.map((role) => (
                <label
                  key={role.value}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedRoles.includes(role.value)}
                    onCheckedChange={(checked) => handleToggleRole(role.value, !!checked)}
                  />
                  <span className="text-sm font-medium">{role.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Representing Entities */}
          <div className="space-y-2">
            <Label>Representing (optional)</Label>
            <EntityLookup
              value={null}
              onChange={handleAddEntity}
              placeholder="Add an entity..."
              excludeIds={representing.map((e) => e.id)}
            />
            {representing.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {representing.map((entity) => (
                  <span
                    key={entity.id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700"
                  >
                    <Building2 className="w-3 h-3" />
                    {entity.name}
                    <button
                      type="button"
                      onClick={() => handleRemoveEntity(entity.id)}
                      className="hover:text-indigo-900 cursor-pointer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this assignment..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedRoles.length === 0 || isSubmitting}
            className="cursor-pointer"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
